import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
  /** Override the headline copy (defaults to "Page not found"). */
  title?: string;
  /** Override the supporting body copy. */
  description?: string;
  /** Destination for the primary CTA (defaults to "/"). */
  ctaHref?: string;
  /** CTA button label (defaults to "Go to home"). */
  ctaLabel?: string;
  className?: string;
};

/**
 * Friendly 404 page wired into the router's `defaultNotFoundComponent`. The
 * `/` route redirects to `/email-accounts`, so a CTA to `/` is the natural
 * "take me back" path. The layout intentionally avoids `AppShell`-style
 * chrome — `AppShell` is rendered by the root route and wraps this content
 * automatically when the not-found surface is reached through a normal
 * navigation.
 */
export function Error404({
  title = 'Page not found',
  description = "The page you're looking for doesn't exist or may have been moved.",
  ctaHref = '/',
  ctaLabel = 'Go to home',
  className,
}: Props) {
  return (
    <div
      role="alert"
      aria-labelledby="error-404-title"
      className={cn(
        'flex h-full w-full flex-col items-center justify-center px-6 py-12 text-center',
        className,
      )}
    >
      <div
        aria-hidden
        className="select-none text-[120px] font-bold leading-none tracking-tight text-primary/80 sm:text-[160px]"
      >
        404
      </div>
      <h1
        id="error-404-title"
        className="mt-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
      >
        {title}
      </h1>
      <p className="mt-3 max-w-md text-sm text-muted-foreground">
        {description}
      </p>
      <div className="mt-8">
        <Button asChild>
          <Link to={ctaHref}>{ctaLabel}</Link>
        </Button>
      </div>
    </div>
  );
}
